import { useEffect, useRef } from 'react'

import { useAtomValue, useSetAtom } from 'jotai'
import { toast } from 'sonner'

import { env } from '@/env'
import { AUTH_ACCESS_TOKEN_STORAGE_KEY } from '@/lib/axios'
import {
  loadingTasksAtom,
  removeExportTaskAtom,
  updateExportTaskAtom
} from '@/lib/export-store'
import { downloadResponseXlsx } from '@/lib/utils'

interface ExportPollingManagerProps {
  pollingInterval?: number
}

function resolveServerFileName(response: Response, url: string) {
  const disposition = response.headers.get('Content-Disposition')
  const match = disposition?.match(
    /filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i
  )
  const fromHeader = match?.[1] || match?.[2]
  if (fromHeader) {
    try {
      return decodeURIComponent(fromHeader)
    } catch {
      return fromHeader
    }
  }

  try {
    const last = new URL(url).pathname.split('/').pop()
    return last ? decodeURIComponent(last) : null
  } catch {
    return null
  }
}

async function downloadFileFromUrl(url: string, fallbackFileName: string) {
  try {
    const response = await fetch(url)
    if (!response.ok) {
      throw new Error(`File tidak ditemukan (${response.status})`)
    }
    const blob = await response.blob()
    const fileName = resolveServerFileName(response, url) || fallbackFileName
    downloadResponseXlsx(blob, fileName)
  } catch (error) {
    console.error('Download error:', error)
    throw error
  }
}

interface ExportStreamResponse {
  status: string
  url?: string
  progress?: number
  message?: string
  file_size?: string
  total_rows?: string
  started_at?: number
}

async function createSSEConnection(
  url: string,
  onMessage: (data: ExportStreamResponse) => void,
  onError: (error: Error) => void,
  body?: any
): Promise<() => void> {
  const token = localStorage.getItem(AUTH_ACCESS_TOKEN_STORAGE_KEY)
  const baseURL = `${env.VITE_SERVER_URL}/api${env.VITE_API_VERSION ? `/${env.VITE_API_VERSION}` : ''}`
  const fullUrl = url.startsWith('http') ? url : `${baseURL}${url}`

  const headers: HeadersInit = {
    Accept: 'text/event-stream',
    'Cache-Control': 'no-cache'
  }

  if (token) {
    headers.Authorization = `Bearer ${token}`
  }

  const controller = new AbortController()
  let reader: ReadableStreamDefaultReader<Uint8Array> | null = null

  try {
    const response = await fetch(fullUrl, {
      method: 'GET',
      headers,
      signal: controller.signal,
      body: body ? JSON.stringify(body) : undefined
    })

    if (!response.ok) {
      toast.error(`SSE connection failed: ${response.statusText}`, {
        id: 'sse-connection-failed'
      })
      throw new Error(`SSE connection failed: ${response.statusText}`)
    }

    if (!response.body) {
      toast.error('Response body is null', {
        id: 'response-body-null'
      })
      throw new Error('Response body is null')
    }

    reader = response.body.getReader()
    const decoder = new TextDecoder()
    let buffer = ''

    const processStream = async () => {
      try {
        while (true) {
          const { done, value } = await reader!.read()

          if (done) {
            break
          }

          buffer += decoder.decode(value, { stream: true })
          const lines = buffer.split('\n')
          buffer = lines.pop() || ''

          for (const line of lines) {
            if (line.startsWith('data: ')) {
              try {
                const jsonData = line.slice(6)
                const data: ExportStreamResponse = JSON.parse(jsonData)
                onMessage(data)
              } catch (parseError) {
                console.error('Error parsing SSE data:', parseError)
              }
            }
          }
        }
      } catch (error: any) {
        if (error.name !== 'AbortError') {
          onError(error)
        }
      }
    }

    processStream().catch(error => {
      if (error.name !== 'AbortError') {
        onError(error)
      }
    })

    return () => {
      controller.abort()
      reader?.cancel().catch(() => {})
    }
  } catch (error: any) {
    onError(error)
    return () => {}
  }
}

export function ExportPollingManager({
  pollingInterval: _pollingInterval
}: ExportPollingManagerProps) {
  const loadingTasks = useAtomValue(loadingTasksAtom)
  const updateTask = useSetAtom(updateExportTaskAtom)
  const removeTask = useSetAtom(removeExportTaskAtom)
  const eventSourceCleanups = useRef<Map<string, () => void>>(new Map())
  const timeoutRefs = useRef<Map<string, number>>(new Map())
  const settledTaskIds = useRef<Set<string>>(new Set())

  useEffect(() => {
    const currentTimeoutRefs = timeoutRefs.current
    const currentCleanups = eventSourceCleanups.current
    const settledIds = settledTaskIds.current

    const failTask = (
      taskId: string,
      toastId: string | number,
      message: string,
      description: string
    ) => {
      if (settledIds.has(taskId)) return
      settledIds.add(taskId)

      toast.error(message, {
        id: toastId,
        description,
        duration: 6000
      })

      const cleanup = currentCleanups.get(taskId)
      if (cleanup) {
        cleanup()
        currentCleanups.delete(taskId)
      }

      const timeoutId = currentTimeoutRefs.get(taskId)
      if (timeoutId) {
        window.clearTimeout(timeoutId)
        currentTimeoutRefs.delete(taskId)
      }

      removeTask(taskId)
    }

    for (const task of loadingTasks) {
      if (
        settledIds.has(task.id) ||
        currentCleanups.has(task.id) ||
        !task.jobId
      ) {
        continue
      }

      const endpointParts = task.apiEndpoint.split('?')
      const baseEndpoint = endpointParts[0]
      const queryParams = endpointParts[1] ? `?${endpointParts[1]}` : ''
      const streamEndpoint = `${baseEndpoint}/stream/${task.jobId}${queryParams}`

      createSSEConnection(
        streamEndpoint,
        (data: ExportStreamResponse) => {
          if (settledIds.has(task.id)) return

          if (data.status === 'success' && data.url) {
            // Server mengirim success dua kali (event `progress` dan `done`),
            // pastikan hanya diproses sekali agar tidak download ganda.
            settledIds.add(task.id)
            updateTask({
              id: task.id,
              updates: { status: 'success', url: data.url }
            })

            const today = new Date()
            const yyyy = today.getFullYear()
            const mm = String(today.getMonth() + 1).padStart(2, '0')
            const dd = String(today.getDate()).padStart(2, '0')
            const fileName = `${task.name} ${yyyy}-${mm}-${dd}.xlsx`
            downloadFileFromUrl(data.url, fileName)
              .then(() => {
                const description = data.file_size
                  ? `File berhasil didownload (${data.file_size}${data.total_rows ? `, ${data.total_rows} rows` : ''})`
                  : 'File berhasil didownload'

                toast.success(data.message || `Export ${task.name} selesai!`, {
                  id: task.toastId,
                  description,
                  duration: 5000
                })

                const cleanup = currentCleanups.get(task.id)
                if (cleanup) {
                  cleanup()
                  currentCleanups.delete(task.id)
                }

                const timeoutId = window.setTimeout(
                  () => removeTask(task.id),
                  1000
                )
                currentTimeoutRefs.set(task.id, timeoutId)
              })
              .catch(error => {
                console.error('Download error:', error)
                toast.error(`Gagal mendownload file: ${error.message}`, {
                  id: task.toastId,
                  duration: 6000
                })
              })
          } else if (data.status === 'error' || data.status === 'failed') {
            failTask(
              task.id,
              task.toastId,
              data.message || `Export ${task.name} gagal`,
              'Terjadi kesalahan saat export'
            )
          } else if (
            (data.status === 'loading' ||
              data.status === 'processing' ||
              data.status === 'progress') &&
            data.progress !== undefined
          ) {
            const roundedProgress =
              typeof data.progress === 'number'
                ? data.progress.toFixed(2)
                : data.progress
            toast.loading(
              data.message || `Export ${task.name} sedang diproses...`,
              {
                id: task.toastId,
                description: `Progress: ${roundedProgress}%`
              }
            )
          } else if (data.progress !== undefined) {
            const roundedProgress =
              typeof data.progress === 'number'
                ? data.progress.toFixed(2)
                : data.progress
            toast.loading(
              data.message || `Export ${task.name} sedang diproses...`,
              {
                id: task.toastId,
                description: `Progress: ${roundedProgress}%`
              }
            )
          }
        },
        (error: Error) => {
          console.error('SSE connection error:', error)
          failTask(
            task.id,
            task.toastId,
            `Export ${task.name} gagal: ${error.message}`,
            'Terjadi kesalahan pada koneksi'
          )
        },
        task.body
      ).then(cleanup => {
        if (!cleanup) return

        if (settledIds.has(task.id)) {
          cleanup()
          return
        }

        currentCleanups.set(task.id, cleanup)
      })
    }

    const activeTaskIds = new Set(loadingTasks.map(t => t.id))
    for (const [taskId, cleanup] of currentCleanups.entries()) {
      if (!activeTaskIds.has(taskId)) {
        cleanup()
        currentCleanups.delete(taskId)
      }
    }

    return () => {
      for (const cleanup of currentCleanups.values()) {
        cleanup()
      }
      currentCleanups.clear()

      currentTimeoutRefs.forEach(timeoutId => {
        window.clearTimeout(timeoutId)
      })
      currentTimeoutRefs.clear()
    }
  }, [loadingTasks, updateTask, removeTask])

  return null
}
